import React, { useState } from 'react';
import { Alert, Box } from '@mui/material';
import { Field, FormikProvider, useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { confirmPasswordReset } from '../../../services/passwordReset';
import { logout } from '../../../redux/features/auth/authSlice';
import { AppDispatch } from '../../../redux/store';
import AuthPageFrame from '../AuthPageFrame';
import RareButton from '../buttons/RareButton';
import { FormPasswordTextField } from '../fields/AuthFields';

function getCookie(name: string) {
  const cookie = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
}

function firstError(data: Record<string, unknown>) {
  if (data.token || data.uid) return 'This reset link is invalid or has expired. Please request a new one.';
  const value = data.new_password2 || data.new_password1 || data.detail;
  return Array.isArray(value) ? value.join(' ') : typeof value === 'string' ? value : 'The password could not be reset. Please try again.';
}

export default function ResetPasswordCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { uidb64, token } = useParams();
  const [requestError, setRequestError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: { password1: '', password2: '' },
    validationSchema: Yup.object({
      password1: Yup.string().required('Password is required'),
      password2: Yup.string().required('Confirm your password').oneOf([Yup.ref('password1')], 'Passwords must match'),
    }),
    onSubmit: async (values) => {
      setRequestError(null);
      try {
        await confirmPasswordReset({
          new_password1: values.password1,
          new_password2: values.password2,
          uid: uidb64,
          token,
        }, getCookie('csrftoken'));
        dispatch(logout());
        navigate('/login');
      } catch (error: any) {
        setRequestError(firstError(error.response?.data || {}));
      }
    },
  });

  return (
    <AuthPageFrame title="Reset Password">
      <FormikProvider value={formik}>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {requestError && <Alert severity="error">{requestError}</Alert>}
          <Field name="password1" label="Password" required autoComplete="new-password" autoFocus component={FormPasswordTextField} />
          <Field name="password2" label="Confirm Password" required autoComplete="new-password" component={FormPasswordTextField} />
          <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #e1e8e6', display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <RareButton variant="outlined" onClick={() => navigate('/login')} sx={{ mr: { sm: 'auto' }, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              Return to Login
            </RareButton>
            <RareButton variant="contained" type="submit" disabled={formik.isSubmitting} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              Confirm
            </RareButton>
          </Box>
        </Box>
      </FormikProvider>
    </AuthPageFrame>
  );
}
